import { CommonModule } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core'
import { RouterOutlet } from '@angular/router'
import { BG_COLOUR, DASHBOARD_URL, DASHBOARD_VALID_ROUTES, ERRORS, TOKEN_KEYS } from '@ui-core/constants/app.constant'
import { TenantProfileInterface } from '@ui-core/interfaces/tenant.interface'
import { UserInterface } from '@ui-core/interfaces/user.interface'
import { AlertService } from '@ui-core/services/alert.service'
import { DomainService } from '@ui-core/services/domain.service'
import { ErrorService } from '@ui-core/services/error.service'
import { HttpService } from '@ui-core/services/http.service'
import { InitService } from '@ui-core/services/init.service'
import { MobileService } from '@ui-core/services/mobile.service'
import { RouteService } from '@ui-core/services/route.service'
import { RouterStateService } from '@ui-core/services/router-state.service'
import { TenantService } from '@ui-core/services/tenant.service'
import { UserService } from '@ui-core/services/user.service'
import { ErrorFullPage } from '@ui-core/ui/error-full-page/error-full-page'
import { Header } from '@ui-core/ui/header/header'
import { Main } from '@ui-core/ui/main/main'
import { SideMenu } from '@ui-core/ui/side-menu/side-menu'
import { EmptyParamsInterface } from '@ui-shared/interfaces/empty-params.interface'
import { urlParseTemplate } from '@ui-utils/url.util'
import { capitalizeFirstLetter } from '@ui-utils/utils'
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular'
import Keycloak from 'keycloak-js'
import { MessageService } from 'primeng/api'
import { ButtonModule } from 'primeng/button'
import { MenubarModule } from 'primeng/menubar'
import { TieredMenuModule } from 'primeng/tieredmenu'
import { Toast } from 'primeng/toast'
import { environment } from './load.env'

@Component({
    selector: 'app-root',
    imports: [
        ButtonModule,
        CommonModule,
        ErrorFullPage,
        Header,
        Main,
        MenubarModule,
        RouterOutlet,
        SideMenu,
        TieredMenuModule,
        Toast
    ],
    templateUrl: './app.html',
    providers: [MessageService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class App {
    private readonly alertService = inject(AlertService)
    private readonly domainService = inject(DomainService)
    private readonly errorService = inject(ErrorService)
    private readonly httpService = inject(HttpService)
    private readonly initService = inject(InitService)
    private readonly keycloakService = inject(Keycloak)
    private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL)
    private readonly messageService = inject(MessageService)
    private readonly mobileService = inject(MobileService)
    private readonly routeService = inject(RouteService)
    private readonly routerStateService = inject(RouterStateService)
    private readonly userService = inject(UserService)
    private readonly tenantService = inject(TenantService)

    areRolesLoaded = signal<boolean>(false)
    isMobileMenuOpen = signal<boolean>(false)
    isProfileLoaded = signal<boolean>(false)
    isUserLoaded = signal<boolean>(false)
    keycloakStatus: string | undefined
    showTenantError = signal<boolean>(false)
    showUI = signal<boolean>(false)
    tenant = signal<string>('')
    bgColor = BG_COLOUR
    isRouteLoaded = signal(false)

    constructor() {
        this.initErrorHandler()
            .initAlertHandler()
            .initRouter()
            .subscribeLogout()
            .initMobileService()
            .checkIsRouteLoaded()

        const keycloak = inject(Keycloak)
        this.checkKeycloakEvents(keycloak)

        this.loadTenantInfo()
    }

    initErrorHandler(): this {
        effect(() => {
            const error = this.errorService.get()()

            if (error === null) {
                return
            }

            const httpError = error as HttpErrorResponse
            if (
                httpError !== undefined &&
                httpError.status !== undefined &&
                (httpError.status === 401 || httpError.status === 403)
            ) {
                this.logout()
            }

            this.messageService.add({
                severity: 'error',
                summary: error?.name,
                detail: error?.message,
                life: 5000
            })
        })

        return this
    }

    initAlertHandler(): this {
        effect(() => {
            const alert = this.alertService.get()()
            if (alert === null) {
                return
            }

            this.messageService.add(alert)
        })

        return this
    }

    initRouter(): this {
        this.routerStateService.subscribeRouterStateChanges()

        return this
    }

    subscribeLogout(): this {
        effect(() => {
            const logout = this.userService.isLogout()()
            if (logout) {
                this.showUI.set(false)
                this.userService.setLogout(false)
                this.logout()
            }
        })

        return this
    }

    initMobileService(): this {
        effect(() => {
            const isMobile = this.mobileService.isMobileMenuOpen()()
            this.isMobileMenuOpen.set(isMobile)
        })

        return this
    }

    checkIsRouteLoaded(): this {
        effect(() => {
            const isRouteLoaded = this.routerStateService.getIsRouteLoaded()
            if (isRouteLoaded) {
                this.isRouteLoaded.set(true)
            }
        })

        return this
    }

    checkKeycloakEvents(keycloak: Keycloak) {
        const keycloakEvent = this.keycloakSignal()

        effect(() => {
            this.keycloakStatus = keycloakEvent.type

            if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
                this.showUI.set(false)
                return
            }

            this.keycloakValidateRoles(keycloak)
            this.keycloakValidateToken(keycloak)
        })
    }

    keycloakValidateRoles(keycloak: Keycloak): void {
        if (
            keycloak &&
            keycloak.resourceAccess &&
            keycloak.resourceAccess[environment.KEYCLOAK_CLIENT_ID] &&
            !this.areRolesLoaded()
        ) {
            this.userService.setRoles(keycloak.resourceAccess[environment.KEYCLOAK_CLIENT_ID].roles)
        }

        this.areRolesLoaded.set(true)
    }

    keycloakValidateToken(keycloak: Keycloak): void {
        if (!this.isUserLoaded()) {
            const tokenParsed = keycloak.tokenParsed

            if (tokenParsed) {
                const user = this.getDataFromToken(tokenParsed)
                this.userService.set(user)
                this.isUserLoaded.set(true)
            }
        }
    }

    getDataFromToken(token: Keycloak.KeycloakTokenParsed): UserInterface {
        const [rootKey] = Object.keys(token[TOKEN_KEYS.organisation] || {})
        const organization = token[TOKEN_KEYS.organisation]?.[rootKey]
        const organizationId = organization?.id
        const organizationAlias = rootKey

        this.tenant.set(organizationAlias)

        return {
            id: token.sub as string,
            username: token[TOKEN_KEYS.preferred_username] as string,
            firstName: capitalizeFirstLetter(token[TOKEN_KEYS.given_name]),
            lastName: capitalizeFirstLetter(token[TOKEN_KEYS.family_name]),
            isLogged: true,
            tenant: {
                organizationId,
                organizationAlias
            }
        }
    }

    loadTenantInfo(): void {
        effect(async () => {
            const isUserLoaded = this.isUserLoaded()
            const areRolesLoaded = this.areRolesLoaded()
            const isProfileLoaded = this.isProfileLoaded()

            const isRouteLoaded = this.isRouteLoaded() === true ? true : false

            if (isUserLoaded && areRolesLoaded && !isProfileLoaded && isRouteLoaded) {
                this.isProfileLoaded.set(true)

                const initialRoute = this.routeService.getCurrentRoute()()
                const currentRoute = DASHBOARD_VALID_ROUTES.includes(initialRoute) ? DASHBOARD_URL : initialRoute

                const tenantRoutes = await this.tenantService.tenantRoutes(this.tenant())

                this.tenantService.isRouteAllowed(tenantRoutes, currentRoute)
                this.tenantService.setCurrentRouteAdditionalConfig(tenantRoutes, currentRoute)

                this.routerStateService.overrideRouterData(tenantRoutes)

                await this.initService.initMenu(this.tenant())
                await this.initService.initUserMenu(this.tenant())

                const url = urlParseTemplate(this.httpService.getUrl.tenants.profile, {})
                this.httpService
                    .get<EmptyParamsInterface, TenantProfileInterface>(url, {})
                    .then((data) => {
                        this.userService.setLogoLight(data.logoLightTheme)
                        this.userService.setLogoDark(data.logoDarkTheme)
                        this.domainService.set(data.domains)

                        this.showUI.set(true)
                    })
                    .catch(() => {
                        this.showTenantError.set(true)
                        throw new Error(
                            ERRORS.tenantFullPage.message.replace(
                                '{PLACEHOLDER}',
                                this.userService.get()?.()?.tenant?.organizationAlias || 'unknown'
                            )
                        )
                    })
            }
        })
    }

    logout() {
        this.isUserLoaded.set(false)
        this.keycloakService.logout()
    }
}
